import { addDays, compareAsc, endOfDay } from "date-fns";
import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { ModalPortal } from "@livechat/design-system-react-components";

import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { config } from "config/config";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { inTrial, isSubscribed } from "helpers/subscription";
import { getDaysLeft } from "helpers/subscription-plans";
import { parseQueryParams } from "helpers/url";
import { AgentActions, selectIsAdmin, selectNextSubscriptionReminderDate } from "store/entities/agents";
import { getBilling } from "store/entities/billing";
import { selectCurrentSubscription } from "store/entities/subscriptions";
import { StackingContextLevel } from "styles/stacking-context-level";
import { plural } from "utils/string";

import { SubscriptionRoutes } from "./constants";

const SubscriptionReminderModal: React.FC = () => {
  const dispatch = useDispatch();
  const queryParams = parseQueryParams(location.search);
  const nextSubReminderDate = useSelector(selectNextSubscriptionReminderDate);
  const subscription = useSelector(selectCurrentSubscription);
  const billing = useSelector(getBilling);
  const daysLeft = getDaysLeft(subscription);
  const isAdmin = useSelector(selectIsAdmin);
  const isTrial = inTrial(subscription);
  const history = useHistory();
  const isInFrame = isInIframe();

  const currentDate = endOfDay(new Date());
  const isRedirectedFromPanel = queryParams.source === "panel";

  const shouldModalBeDisplayed =
    isTrial &&
    daysLeft <= 7 &&
    !compareAsc(currentDate, nextSubReminderDate) &&
    !isSubscribed(billing) &&
    isAdmin &&
    !isRedirectedFromPanel;

  const handlePostponeReminder = (): void => {
    trackEngagement("Trial ends reminder: modal displayed", EventPlace.Subscription, {
      daysLeftTillTrialEnd: daysLeft,
      isRedirectedFromPanel,
    });

    dispatch(
      AgentActions.setFlag({
        name: "nextSubscriptionReminderDate_9c4a2ae9dbf526645b366b3c27e518dc",
        value: addDays(currentDate, 1).toISOString(),
      })
    );
  };

  const handleSubscribeButtonClick = (): void => {
    trackEngagement("Trial ends reminder: Subscribe now button clicked", EventPlace.Subscription, {
      daysLeftTillTrialEnd: daysLeft,
    });

    history.push(SubscriptionRoutes.Plans);
    handlePostponeReminder();
  };

  useEffect(() => {
    if (isRedirectedFromPanel) {
      handlePostponeReminder();
    }
  }, [isRedirectedFromPanel]);

  return shouldModalBeDisplayed ? (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <ConfirmActionModal
        heading={`${daysLeft} ${plural(daysLeft, "day", "days")} left in your trial!`}
        primaryAction={{
          label: "Subscribe now",
          onClick: isInFrame ? handlePostponeReminder : handleSubscribeButtonClick,
          actionType: "primary",
          href: isInFrame
            ? undefined
            : `${config.appUrl}/subscription?utm_source=my.livechatinc.com&utm_medium=referral&utm_campaign=helpdesk-in-livechat&utm_term=open-helpdesk`,
          target: isInFrame ? undefined : "_blank",
        }}
        onClose={handlePostponeReminder}
      >
        Subscribe now and keep using HelpDesk to help your customers in an organized way. It&apos;ll only take you a
        minute.
      </ConfirmActionModal>
    </ModalPortal>
  ) : null;
};

export default SubscriptionReminderModal;
